/* Keep purchasable products ahead of sold-out products across the full list. */
(() => {
  if (window.acelyaStockOrderInitialized) return;
  window.acelyaStockOrderInitialized = true;
  const selector = 'template[data-acelya-stock-order]';
  const processed = new WeakSet();
  const products = (grid) => Array.from(grid.children).filter((item) => item.matches('li[data-acelya-product-id]'));
  const stockFirst = (items) => items.filter((item) => item.dataset.acelyaAvailable === 'true').concat(items.filter((item) => item.dataset.acelyaAvailable !== 'true'));

  async function orderList(marker) {
    if (processed.has(marker)) return;
    processed.add(marker);
    const grid = marker.parentElement;
    const page = Number(marker.dataset.page);
    const pages = Number(marker.dataset.pages);
    const size = Number(marker.dataset.pageSize);
    const total = Number(marker.dataset.total);
    const featured = marker.dataset.mode === 'featured';
    if (!grid || !size || !pages) return;
    if (pages === 1) {
      marker.dataset.state = 'ready';
      return;
    }
    marker.dataset.state = 'loading';
    grid.setAttribute('aria-busy', 'true');
    const baseUrl = new URL(marker.dataset.sourcePath, window.location.origin);
    baseUrl.search = window.location.search;
    baseUrl.searchParams.delete('sections');
    baseUrl.searchParams.set('section_id', marker.dataset.sectionId);
    const pageItems = new Array(pages);
    pageItems[page - 1] = products(grid);
    const pending = Array.from({ length: pages }, (_, index) => index + 1).filter((number) => number !== page);
    let cursor = 0;

    async function readPage(number) {
      const url = new URL(baseUrl);
      url.searchParams.set(marker.dataset.pageParam, String(number));
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { signal: controller.signal, cache: 'no-store' });
        if (!response.ok) throw new Error('Collection page could not be loaded');
        return new DOMParser().parseFromString(await response.text(), 'text/html');
      } finally {
        clearTimeout(timeout);
      }
    }

    async function loadPage(number) {
      const html = await readPage(number);
      const result = Array.from(html.querySelectorAll(selector)).find((element) => element.dataset.sectionId === marker.dataset.sectionId);
      if (!result || Number(result.dataset.page) !== number || Number(result.dataset.total) !== total || Number(result.dataset.pageSize) !== size) {
        throw new Error('Collection pagination changed');
      }
      pageItems[number - 1] = products(result.parentElement);
    }

    try {
      await Promise.all(Array.from({ length: Math.min(4, pending.length) }, async () => {
        while (cursor < pending.length) {
          const number = pending[cursor++];
          await loadPage(number);
        }
      }));
      if (!marker.isConnected) return;
      const all = pageItems.flat();
      if (all.length !== total || new Set(all.map((item) => item.dataset.acelyaProductId)).size !== total) {
        throw new Error('Collection product count changed: total=' + total + ', rendered=' + all.length + ', unique=' + new Set(all.map((item) => item.dataset.acelyaProductId)).size + ', pages=' + pageItems.map((items) => items.length).join('/')); 
      }
      const offset = featured ? 0 : (page - 1) * size;
      const ordered = stockFirst(all).slice(offset, offset + size);
      ordered.forEach((item, index) => {
        if (featured) item.id = 'Slide-' + marker.dataset.sectionId + '-' + (index + 1);
        item.style.setProperty('--animation-order', String(index + 1));
        if (item.classList.contains('scroll-trigger')) item.classList.add('scroll-trigger--cancel');
        item.querySelectorAll('img').forEach((image) => { image.loading = index < 2 ? 'eager' : 'lazy'; });
      });
      grid.replaceChildren(marker, ...ordered);
      marker.dataset.state = 'ready';
      grid.closest('slider-component')?.resetPages?.();
    } catch (error) {
      // Refresh once if inventory/catalog changes while pages are loading.
      if (marker.isConnected && marker.dataset.retry !== '1' && /^(Collection product count changed|Collection pagination changed)/.test(error.message)) {
        try {
          const html = await readPage(page);
          const freshMarker = Array.from(html.querySelectorAll(selector)).find((element) => element.dataset.sectionId === marker.dataset.sectionId);
          if (marker.isConnected && freshMarker) {
            freshMarker.dataset.retry = '1';
            const container = grid.closest('#ProductGridContainer');
            const freshContainer = html.getElementById('ProductGridContainer');
            if (container && freshContainer) {
              container.replaceChildren(...Array.from(freshContainer.childNodes));
              ['ProductCount', 'ProductCountDesktop'].forEach((id) => {
                const target = document.getElementById(id);
                const source = html.getElementById(id);
                if (target && source) {
                  target.innerHTML = source.innerHTML;
                  Object.assign(target.dataset, source.dataset);
                }
              });
            } else {
              grid.replaceChildren(...Array.from(freshMarker.parentElement.childNodes));
            }
            return;
          }
        } catch (refreshError) {
          console.warn('Açelya stock ordering: the list refresh could not finish.', refreshError);
        }
      }
      if (marker.isConnected) marker.dataset.state = 'fallback';
      console.warn('Açelya stock ordering: showing the server-rendered product list.', error);
    } finally {
      if (marker.isConnected) grid.removeAttribute('aria-busy');
    }
  }

  function scan(root) {
    if (root.matches?.(selector)) orderList(root);
    root.querySelectorAll?.(selector).forEach(orderList);
  }
  function start() {
    const root = document.getElementById('MainContent') || document.body;
    scan(root);
    new MutationObserver((records) => {
      records.forEach((record) => record.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) scan(node);
      }));
    }).observe(root, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', (event) => scan(event.target));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
